/**
 * Gestion de la permission microphone + priming audio Safari.
 * Lib pure, pas de React.
 */

const isDev = import.meta.env.DEV
const log = (...args: unknown[]) => {
  if (isDev) console.log(...args)
}

/**
 * Demande la popup native du navigateur pour la permission micro.
 * DOIT être appelé depuis un geste utilisateur (click/tap).
 */
export async function requestMicrophonePermission(): Promise<boolean> {
  try {
    log('🎤 Demande de permission microphone (popup native)…')
    const stream = await navigator.mediaDevices.getUserMedia({
      audio: {
        echoCancellation: true,
        noiseSuppression: true,
        autoGainControl: true,
      },
    })
    stream.getTracks().forEach((track) => track.stop())
    log('✅ Permission microphone accordée')
    return true
  } catch (error) {
    const err = error as { name?: string }
    console.error('❌ Erreur accès microphone:', error)
    if (err.name === 'NotAllowedError' || err.name === 'PermissionDeniedError') {
      console.warn('🚫 Utilisateur a refusé la permission microphone')
    } else if (err.name === 'NotFoundError') {
      console.error('🎤 Aucun microphone détecté sur l\'appareil')
    } else if (err.name === 'NotReadableError') {
      console.error('🔒 Microphone déjà utilisé par une autre application')
    }
    return false
  }
}

/**
 * Vérifie si la permission micro est accordée sans déclencher de popup.
 */
export async function checkMicrophonePermissionStatus(): Promise<
  'granted' | 'denied' | 'prompt' | 'unknown'
> {
  try {
    if ('permissions' in navigator) {
      const status = await navigator.permissions.query({
        name: 'microphone' as PermissionName,
      })
      return status.state as 'granted' | 'denied' | 'prompt'
    }
  } catch {
    // API Permissions non dispo
  }
  return 'unknown'
}

export async function isMicrophoneDefinitelyBlocked(): Promise<boolean> {
  const status = await checkMicrophonePermissionStatus()
  if (status === 'denied') return true
  if (status === 'granted') return false

  try {
    const stream = await navigator.mediaDevices.getUserMedia({ audio: true })
    stream.getTracks().forEach((track) => track.stop())
    return false
  } catch (error) {
    const err = error as { name?: string }
    return err.name === 'NotAllowedError' || err.name === 'PermissionDeniedError'
  }
}

/**
 * Priming audio pour Safari : débloque le micro ET resume l'AudioContext Twilio.
 * À appeler dans le même click handler que l'accept de l'appel.
 */
export async function primeAudioForSafari(): Promise<boolean> {
  try {
    log('🎵 Priming audio pour Safari…')
    const stream = await navigator.mediaDevices.getUserMedia({ audio: true })
    stream.getTracks().forEach((track) => track.stop())

    try {
      const w = window as unknown as {
        twilioAudio?: { context?: { state?: string; resume?: () => Promise<void> } }
      }
      if (w.twilioAudio?.context?.state === 'suspended') {
        await w.twilioAudio.context.resume?.()
      }
    } catch {
      // Non bloquant
    }
    log('✅ Audio primé')
    return true
  } catch (error) {
    console.error('❌ Erreur priming audio:', error)
    return false
  }
}

export function isSecureContext(): boolean {
  return window.isSecureContext
}

export type Platform =
  | 'chrome-desktop'
  | 'chrome-android'
  | 'safari-ios'
  | 'safari-desktop'
  | 'firefox'
  | 'pwa-ios'
  | 'pwa-android'
  | 'unknown'

export function detectPlatform(): Platform {
  const ua = navigator.userAgent.toLowerCase()
  const isStandalone =
    window.matchMedia('(display-mode: standalone)').matches ||
    (window.navigator as unknown as { standalone?: boolean }).standalone === true

  if (isStandalone) {
    if (/iphone|ipad|ipod/.test(ua)) return 'pwa-ios'
    if (/android/.test(ua)) return 'pwa-android'
  }

  if (/iphone|ipad|ipod/.test(ua) && /safari/.test(ua) && !/chrome/.test(ua))
    return 'safari-ios'
  if (/android/.test(ua) && /chrome/.test(ua)) return 'chrome-android'
  if (/chrome/.test(ua) && !/android/.test(ua)) return 'chrome-desktop'
  if (/safari/.test(ua) && !/chrome/.test(ua) && !/iphone|ipad|ipod/.test(ua))
    return 'safari-desktop'
  if (/firefox/.test(ua)) return 'firefox'
  return 'unknown'
}

export interface MicrophoneInstructions {
  title: string
  steps: string[]
}

export function getMicrophoneInstructions(platform?: Platform): MicrophoneInstructions {
  const p = platform || detectPlatform()
  switch (p) {
    case 'chrome-desktop':
      return {
        title: 'Autoriser le microphone sur Chrome',
        steps: [
          "Cliquez sur l'icône de cadenas 🔒 dans la barre d'adresse",
          'Cherchez "Microphone" dans la liste',
          'Changez à "Autoriser"',
          'Rechargez la page',
        ],
      }
    case 'chrome-android':
      return {
        title: 'Autoriser le microphone sur Android',
        steps: [
          "Appuyez sur l'icône de cadenas dans la barre d'adresse",
          'Appuyez sur "Autorisations"',
          'Activez le "Microphone"',
          'Rechargez la page',
        ],
      }
    case 'safari-ios':
      return {
        title: 'Autoriser le microphone sur iPhone',
        steps: [
          'Ouvrez Réglages iOS → Safari',
          "Faites défiler jusqu'à \"Microphone\"",
          'Sélectionnez "Demander" ou "Autoriser"',
          "Revenez sur l'application",
        ],
      }
    case 'pwa-ios':
      return {
        title: 'Autoriser le microphone (App installée)',
        steps: [
          'Ouvrez Réglages iOS',
          'Faites défiler et trouvez "Callizy"',
          'Activez "Microphone"',
          "Relancez l'application",
        ],
      }
    case 'pwa-android':
      return {
        title: 'Autoriser le microphone (App installée)',
        steps: [
          'Ouvrez Paramètres Android → Applications',
          'Trouvez "Chrome" (navigateur source)',
          'Autorisations → Microphone → Autoriser',
          "Relancez l'application",
        ],
      }
    case 'safari-desktop':
      return {
        title: 'Autoriser le microphone sur Safari',
        steps: [
          'Safari → Réglages pour ce site web',
          'Microphone → Autoriser',
          'Rechargez la page',
        ],
      }
    case 'firefox':
      return {
        title: 'Autoriser le microphone sur Firefox',
        steps: [
          "Cliquez sur l'icône de cadenas 🔒",
          'Cliquez sur "Plus d\'informations"',
          'Permissions → Microphone → Autoriser',
          'Rechargez la page',
        ],
      }
    default:
      return {
        title: 'Autoriser le microphone',
        steps: [
          "Cliquez sur l'icône de sécurité dans la barre d'adresse",
          'Cherchez les paramètres de microphone',
          "Autorisez l'accès au microphone",
          'Rechargez la page',
        ],
      }
  }
}
